/**
 * AlertCountIndicator — one title-bar meter for one KIND of alert.
 *
 * THE PROBLEM IT SOLVES
 * ---------------------
 * Trinetra raises two entirely different things it calls alerts, and the title bar showed one
 * of them behind a bell:
 *
 *   • AI ANALYTICS alerts — what the cameras and the models saw. Intrusion, fire, a face on a
 *     watchlist. An operator acts on these, and they live on the Alert List / board.
 *   • SYSTEM alerts — what the software and the machines are doing. A crashed service, a node
 *     that stopped reporting, a disk filling. An administrator acts on these, on the System
 *     Alerts page.
 *
 * A single bell with a single red number could be either, and a number that could be either is
 * a number nobody can act on: "7" might be seven people in a restricted area or seven service
 * restarts. So each kind gets its OWN meter, with its own icon, its own destination and a
 * tooltip that names the domain outright — a shield for what the cameras saw, a server stack
 * for what the system is doing. Two meters side by side also make the distinction impossible
 * to miss, which one merged badge could never do however it was worded.
 *
 * WHAT THE NUMBERS MEAN
 * ---------------------
 * The badge counts what still NEEDS SOMEBODY — alerts nobody has picked up — never a lifetime
 * total, and never every open alert. That distinction is the difference between a badge that
 * responds to work and one that looks broken: counting all open alerts meant acknowledging a
 * screenful of them moved the number not at all, and the moment an operator watches for the
 * number to move is the moment they just acted on it. What is already being worked on is
 * reported on its own line (`secondary`) rather than hidden.
 *
 * The severity split is on hover rather than on the badge, because a title bar has room for
 * one number and an operator only needs the split once they have decided the number is worth
 * looking at. The badge's COLOUR carries the worst severity present, so the split is not
 * needed to know how bad it is.
 */
import { ServerStackIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import Tooltip from './Tooltip';

/** Which of the two alert domains this meter reports. */
export type AlertDomain = 'ai' | 'system';

/** One severity row of the hover breakdown. Labels are the app's, not ours — the two domains
 *  genuinely use different severity vocabularies (AI: critical/warning/info; system errors add
 *  'error'), and inventing a shared one here would misreport both. */
export interface AlertSeverityCount {
  label: string;
  count: number;
  /** Decides the dot's colour and, for the worst non-zero row, the badge's. */
  tone: 'critical' | 'warning' | 'info';
}

const TONE_DOT: Record<AlertSeverityCount['tone'], string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-400',
  info: 'bg-blue-500',
};

const TONE_BADGE: Record<AlertSeverityCount['tone'], string> = {
  critical: 'bg-red-500 text-white',
  warning: 'bg-amber-400 text-amber-950',
  info: 'bg-blue-500 text-white',
};

const DOMAIN: Record<AlertDomain, { icon: typeof ShieldExclamationIcon; title: string; what: string; open: string }> = {
  ai: {
    icon: ShieldExclamationIcon,
    title: 'AI analytics alerts',
    what: 'What the cameras and models detected',
    open: 'Open the alert list',
  },
  system: {
    icon: ServerStackIcon,
    title: 'System & service alerts',
    what: 'Service crashes, health and node problems',
    open: 'Open system alerts',
  },
};

export interface AlertCountIndicatorProps {
  domain: AlertDomain;
  /** Open alerts. `null` while loading, or when the current scope cannot answer — see `note`. */
  total: number | null;
  /** Severity split for the tooltip. Rows with a zero count are shown too, so the absence of a
   *  severity is stated rather than left to be inferred from a missing line. */
  breakdown?: AlertSeverityCount[];
  onOpen: () => void;
  /** Replaces the counts in the tooltip when `total` is null for a REASON worth stating (the
   *  cloud's All Sites scope, where no single site's number would be true). */
  note?: string;
  /**
   * One extra line under the split — for the alerts NOT in the headline because somebody has
   * already taken them ("Acknowledged, still open: 4"). Present so that a falling badge cannot
   * be read as alerts having disappeared.
   */
  secondary?: { label: string; count: number };
  className?: string;
}

export default function AlertCountIndicator({
  domain,
  total,
  breakdown = [],
  onOpen,
  note,
  secondary,
  className = '',
}: AlertCountIndicatorProps) {
  const meta = DOMAIN[domain];
  const Icon = meta.icon;

  // Worst severity actually present — the badge's colour. Falls back to info, so a count with
  // no breakdown (an endpoint that only returns a total) still renders.
  const worst = (['critical', 'warning', 'info'] as const).find((t) =>
    breakdown.some((b) => b.tone === t && b.count > 0),
  );
  const badgeTone = TONE_BADGE[worst || 'info'];

  const tip = (
    <div className="min-w-[11rem] space-y-1">
      <div className="flex items-center gap-1.5 font-semibold">
        <Icon className="h-3.5 w-3.5 flex-none" aria-hidden />
        {meta.title}
      </div>
      <div className="text-[11px] font-normal opacity-70">{meta.what}</div>
      {total == null ? (
        <div className="pt-0.5 text-[11px] font-normal opacity-80">{note || 'Loading…'}</div>
      ) : (
        <>
          <div className="space-y-0.5 border-t border-border pt-1">
            {breakdown.length > 0 ? (
              breakdown.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3 font-normal">
                  <span className="flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 flex-none rounded-full ${TONE_DOT[row.tone]}`} />
                    {row.label}
                  </span>
                  <span className="font-semibold tabular-nums">{row.count}</span>
                </div>
              ))
            ) : (
              <div className="font-normal">{total} open</div>
            )}
          </div>
          {/* Spelled out even when it is zero: "nothing waiting" is information, and a silent
              meter is indistinguishable from a broken one. */}
          <div className="flex items-center justify-between gap-3 border-t border-border pt-1 font-semibold">
            <span>Needs attention</span>
            <span className="tabular-nums">{total}</span>
          </div>
          {secondary && (
            <div className="flex items-center justify-between gap-3 font-normal opacity-80">
              <span>{secondary.label}</span>
              <span className="tabular-nums">{secondary.count}</span>
            </div>
          )}
          <div className="pt-0.5 text-[11px] font-normal opacity-70">{meta.open} →</div>
        </>
      )}
    </div>
  );

  return (
    <Tooltip label={tip} maxWidth={280} className={className}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${meta.title}${total != null ? `: ${total} needing attention` : ''}`}
        className="relative rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Icon className="h-5 w-5" />
        {total != null && total > 0 && (
          <span
            className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums ${badgeTone}`}
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
    </Tooltip>
  );
}
